import { useState, useEffect } from 'react'

import { getGifsByEmotionalTags } from '../../../gifs/services/gifService'
import { GifThumbnail } from '../../components/GifThumbnail'
import type { Gif } from '../../../gifs/types'

interface TagPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  tag1: string
  tag2: string
  count1: number
  count2: number
}

export function TagPreviewModal({
  isOpen,
  onClose,
  tag1,
  tag2,
  count1,
  count2,
}: TagPreviewModalProps) {
  const [tag1Gifs, setTag1Gifs] = useState<Gif[]>([])
  const [tag2Gifs, setTag2Gifs] = useState<Gif[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return

    const loadGifs = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const [gifs1, gifs2] = await Promise.all([
          getGifsByEmotionalTags([tag1]),
          getGifsByEmotionalTags([tag2]),
        ])

        setTag1Gifs(gifs1.slice(0, 12)) // Limit to 12 gifs for performance
        setTag2Gifs(gifs2.slice(0, 12))
      } catch (err) {
        console.error('Error loading gifs for tag preview:', err)
        setError('Failed to load gifs for preview')
      } finally {
        setIsLoading(false)
      }
    }

    loadGifs()
  }, [isOpen, tag1, tag2])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">
              Tag Preview: &quot;{tag1}&quot; vs &quot;{tag2}&quot;
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl font-bold"
            >
              ×
            </button>
          </div>
          <p className="text-gray-600 text-sm mt-2">
            Review GIFs using these tags to decide if they should be merged
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading GIFs...</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Tag 1 Column */}
              <div>
                <h4 className="text-md font-semibold mb-3 flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                    &quot;{tag1}&quot;
                  </span>
                  <span className="text-gray-500 text-sm">
                    ({count1} total, showing {tag1Gifs.length})
                  </span>
                </h4>
                {tag1Gifs.length > 0 ? (
                  <div className="grid grid-cols-3 gap-3">
                    {tag1Gifs.map((gif) => (
                      <GifThumbnail
                        key={gif.id}
                        gif={gif}
                        className="w-full aspect-square"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-500 text-center py-8 border-2 border-dashed border-gray-200 rounded">
                    No GIFs found with this tag
                  </div>
                )}
              </div>

              {/* Tag 2 Column */}
              <div>
                <h4 className="text-md font-semibold mb-3 flex items-center gap-2">
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                    &quot;{tag2}&quot;
                  </span>
                  <span className="text-gray-500 text-sm">
                    ({count2} total, showing {tag2Gifs.length})
                  </span>
                </h4>
                {tag2Gifs.length > 0 ? (
                  <div className="grid grid-cols-3 gap-3">
                    {tag2Gifs.map((gif) => (
                      <GifThumbnail
                        key={gif.id}
                        gif={gif}
                        className="w-full aspect-square"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-500 text-center py-8 border-2 border-dashed border-gray-200 rounded">
                    No GIFs found with this tag
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Compare the GIFs above to determine if these tags represent the
              same emotional concept
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Close Preview
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
